import React, { useState, useEffect, useRef } from 'react';
import { LearningTopic, LearningSession, LearningSessionMessage, SessionAttachment } from '../types';
import { 
  getLearningTopics, 
  getLearningSessions, 
  syncLearningDataFromCloud,
  syncLearningDataFromApi,
  subscribeToLearningData,
  createTopic, updateTopic, deleteTopic, 
  createSession, deleteSession, addMessageToSession, deleteMessageFromSession,
  addAttachmentToSession, deleteAttachmentFromSession
} from '../lib/learningStorage';
import { 
  saveAttachmentBlob, resolveSafeFileUrl, getSafeBrowserUrl, triggerFileDownload, 
  extractTextFromDataUrl, extractTextFromFile 
} from '../lib/attachmentStorage';
import { uploadFileToWorker, getWorkerFileUrl, deleteFileFromWorker } from '../lib/workerFileStorage';
import { deleteCloudFileRecord } from '../lib/cloudFileStorage';
import { sendWorkerAiRequest, sendWorkerAiFullRequest, WorkerAiFullResponse } from '../lib/workerAi';
import { 
  isSafeVideoUrl, parseVideoMetadata, extractYouTubeVideoId, getEmbedVideoUrl 
} from '../lib/videoUtils';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { formatGregorianDate } from '../lib/dateUtils';
import { 
  GraduationCap, Plus, Search, BookOpen, Trash2, Edit3, ChevronLeft, 
  Sparkles, Calendar, FileText, Check, Copy, RefreshCw, Layers, AlignRight, AlignLeft,
  ListPlus, Lightbulb, Send, MessageSquare, User, StickyNote, HelpCircle,
  Video, Presentation, File, UploadCloud, X, Eye, Download, Play, Film, Paperclip, Folder,
  Globe, Link, Youtube, Loader2, Cpu
} from 'lucide-react';
import SharedFileManager from './SharedFileManager';
import { VoiceInputButton } from './VoiceInputButton';

// Attachment Card with Interactive Player / Document Viewer Modal & AI Quick Actions
function AttachmentCard({ 
  attachment, 
  sessionId,
  onDelete,
  onAiPrompt
}: { 
  attachment: SessionAttachment; 
  sessionId?: string;
  onDelete?: () => void;
  onAiPrompt?: (promptText: string) => void;
}) {
  const { t } = useLanguage();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isVideoLink = attachment.type === 'video' || Boolean(attachment.provider) || Boolean(attachment.embedUrl) || Boolean(attachment.isExternalLink);
  const effectiveFileUrl = isVideoLink 
    ? (attachment.fileUrl || '') 
    : getWorkerFileUrl(attachment.key || attachment.fileId, attachment.fileUrl);
  const [resolvedUrl, setResolvedUrl] = useState<string>(effectiveFileUrl);

  useEffect(() => {
    if (isVideoLink) {
      setResolvedUrl(attachment.fileUrl || '');
    } else {
      const url = attachment.key
        ? getWorkerFileUrl(attachment.key)
        : attachment.fileUrl?.includes('workers.dev')
          ? attachment.fileUrl
          : getWorkerFileUrl(attachment.fileId || attachment.id);
      setResolvedUrl(url || effectiveFileUrl);
    }
  }, [isVideoLink, attachment.key, attachment.fileId, attachment.id, attachment.fileUrl, effectiveFileUrl]);

  const getBadgeColor = () => {
    switch (attachment.type) {
      case 'video': return 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300';
      case 'ppt': return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
      case 'word': return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
      case 'pdf': return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
      case 'image': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getIcon = () => {
    switch (attachment.type) {
      case 'video': return <Video className="w-5 h-5 text-purple-500" />;
      case 'ppt': return <Presentation className="w-5 h-5 text-amber-500" />;
      case 'word': return <FileText className="w-5 h-5 text-blue-500" />;
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'image': return <Film className="w-5 h-5 text-emerald-500" />;
      default: return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customPromptInput, setCustomPromptInput] = useState('');

  const handleTriggerAiAction = (actionType: 'translate_fa' | 'translate_sv' | 'summarize' | 'quiz' | 'custom', userCustomText?: string) => {
    if (!onAiPrompt) return;
    const extracted = attachment.extractedText || '';
    const textContext = extracted 
      ? `\n\n${t('learningExtractedSummaryDoc', 'متن استخراج‌شده از این سند:')}\n${extracted}` 
      : `\n\n${t('learningAttFileNameLabel', 'نام فایل پیوست: {{name}}', { name: attachment.name })}`;

    switch (actionType) {
      case 'translate_fa':
        onAiPrompt(t('learningAiPromptTranslateFa', 'لطفاً محتوای سند PDF «{{name}}» را به صورت دقیق، کلاسی و روان به زبان فارسی ترجمه کن و خلاصه‌ای از نکات کلیدی آن را بنویس.', { name: attachment.name }) + textContext);
        break;
      case 'translate_sv':
        onAiPrompt(t('learningAiPromptTranslateSv', 'Vänligen översätt innehållet i PDF-dokumentet «{{name}}» till svenska med pedagogiska förklaringar och viktigaste punkter.', { name: attachment.name }) + textContext);
        break;
      case 'summarize':
        onAiPrompt(t('learningAiPromptSummarize', 'لطفاً خلاصه‌ای جامع و ۵ نقطه کلیدی و مهم همراه با جدول واژگان یا نکات از سند PDF «{{name}}» استخراج کن.', { name: attachment.name }) + textContext);
        break;
      case 'quiz':
        onAiPrompt(t('learningAiPromptQuiz', 'بر اساس محتوای سند PDF «{{name}}»، ۵ سوال آزمایشی و تست کاربردی همراه با پاسخ‌نامه تشریحی برای سنجش یادگیری طراحی کن.', { name: attachment.name }) + textContext);
        break;
      case 'custom':
        if (userCustomText && userCustomText.trim()) {
          onAiPrompt(t('learningAiPromptCustom', 'بر اساس سند PDF «{{name}}»، درخواست سفارشی زیر را به صورت دقیق، جامع و کامل تحلیل و پاسخ بده:\n\n📌 درخواست کاربر: {{text}}', { name: attachment.name, text: userCustomText.trim() }) + textContext);
        }
        break;
    }
  };

  const handleOpenFileInNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // resolvedUrl is already the full Cloudflare Worker URL
    const targetUrl = resolvedUrl || getWorkerFileUrl(attachment.key || attachment.fileId || attachment.id);
    if (!targetUrl) {
      alert(t('learningFileNotfound', 'فایل "{{name}}" یافت نشد.', { name: attachment.name }));
      return;
    }
    const win = window.open(targetUrl, '_blank');
    if (!win) {
      triggerFileDownload(attachment.fileUrl, attachment.name, attachment.id, attachment.key);
    }
  };

  return (
    <div className="bg-slate-50 dark:bg-gray-800/80 border border-slate-200 dark:border-gray-700 rounded-xl p-3 space-y-2.5 relative group">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-2 bg-white dark:bg-gray-900 rounded-lg shadow-xs shrink-0">
            {getIcon()}
          </div>
          <div className="min-w-0">
            <h5 className="text-xs font-bold text-gray-900 dark:text-white truncate" title={attachment.name}>
              {attachment.name}
            </h5>
            <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
              <span className={`px-1.5 py-0.2 rounded font-bold uppercase ${getBadgeColor()}`}>
                {attachment.type}
              </span>
              {attachment.fileSize && <span>{attachment.fileSize}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="p-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 rounded-lg transition-colors cursor-pointer text-xs font-bold flex items-center gap-1"
            title={t('learningActionViewOnline', 'مشاهده / پخش آنلاین فایل')}
          >
            {attachment.type === 'video' ? <Play className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{attachment.type === 'video' ? t('learningActionPlayVideo', 'پخش ویدیو') : t('learningActionViewDoc', 'مشاهده سند')}</span>
          </button>

          <button
            type="button"
            onClick={() => triggerFileDownload(attachment.fileUrl, attachment.name, attachment.id, attachment.key)}
            className="p-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-600 dark:text-gray-300 rounded-lg transition-colors cursor-pointer"
            title={t('learningActionDownload', 'دانلود فایل')}
          >
            <Download className="w-3.5 h-3.5" />
          </button>

          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
              title={t('learningActionDeleteAttachment', 'حذف پیوست')}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Direct Video Player / Embed Player Component */}
      {attachment.type === 'video' && (() => {
        const embedUrl = getEmbedVideoUrl(resolvedUrl || attachment.fileUrl);
        const isInstagram = (resolvedUrl || attachment.fileUrl).includes('instagram.com') || (resolvedUrl || attachment.fileUrl).includes('instagr.am');
        if (embedUrl) {
          return (
            <div className="mt-2 overflow-hidden rounded-xl bg-black border border-gray-800 shadow-md">
              <div className={`relative w-full ${isInstagram ? 'h-[460px] max-w-sm mx-auto' : 'aspect-video'}`}>
                <iframe
                  src={embedUrl}
                  title={attachment.name}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          );
        }
        return (
          <div className="mt-2 overflow-hidden rounded-xl bg-black border border-gray-800 shadow-md">
            <video 
              src={resolvedUrl || attachment.fileUrl} 
              controls 
              preload="metadata"
              className="w-full max-h-72 rounded-xl object-contain"
            >
              {t('learningVideoUnsupported', 'مرورگر شما از پخش ویدیو پشتیبانی نمی‌کند.')}
            </video>
          </div>
        );
      })()}

      {/* Interactive AI PDF Quick Action Buttons */}
      {(attachment.type === 'pdf' || attachment.type === 'word' || attachment.type === 'ppt') && onAiPrompt && (
        <div className="pt-2 border-t border-gray-200/60 dark:border-gray-700/60 space-y-1.5">
          <div className="flex items-center justify-between text-[10px] font-bold text-indigo-600 dark:text-indigo-400">
            <span className="flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-500" />
              {t('learningAiPromptFromPdf', 'درخواست هوش مصنوعی از این PDF:')}
            </span>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto pb-1 no-scrollbar text-[10px]">
            <button
              type="button"
              onClick={() => handleTriggerAiAction('translate_fa')}
              className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 rounded-md font-bold whitespace-nowrap border border-indigo-200 dark:border-indigo-800 transition-colors cursor-pointer"
            >
              🇮🇷 {t('learningAiTransFa', 'ترجمه به فارسی')}
            </button>
            <button
              type="button"
              onClick={() => handleTriggerAiAction('translate_sv')}
              className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 rounded-md font-bold whitespace-nowrap border border-purple-200 dark:border-purple-800 transition-colors cursor-pointer"
            >
              🇸🇪 {t('learningAiTransSv', 'ترجمه به سوئدی')}
            </button>
            <button
              type="button"
              onClick={() => handleTriggerAiAction('summarize')}
              className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-md font-bold whitespace-nowrap border border-emerald-200 dark:border-emerald-800 transition-colors cursor-pointer"
            >
              📝 {t('learningAiSummarize', 'خلاصه‌سازی')}
            </button>
            <button
              type="button"
              onClick={() => handleTriggerAiAction('quiz')}
              className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-md font-bold whitespace-nowrap border border-amber-200 dark:border-amber-800 transition-colors cursor-pointer"
            >
              ✍️ {t('learningAiQuiz', 'طراحی کوییز')}
            </button>
            <button
              type="button"
              onClick={() => setIsCustomModalOpen(true)}
              className="px-2 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300 rounded-md font-bold whitespace-nowrap border border-sky-200 dark:border-sky-800 transition-colors cursor-pointer flex items-center gap-1"
            >
              <Lightbulb className="w-3 h-3 text-amber-500" />
              💡 {t('learningAiCustom', 'درخواست سفارشی...')}
            </button>
          </div>
        </div>
      )}

      {/* Custom AI Prompt Modal */}
      {isCustomModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-950/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 font-display">
                <Sparkles className="w-5 h-5 text-amber-500" />
                {t('learningAiModalTitle', 'درخواست هوش مصنوعی از فایل «{{name}}»', { name: attachment.name })}
              </h3>
              <button
                type="button"
                onClick={() => setIsCustomModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                {t('learningAiModalLabel', 'درخواست یا دستور دلخواه خود را برای هوش مصنوعی درباره این سند بنویسید:')}
              </label>
              <textarea
                rows={4}
                value={customPromptInput}
                onChange={(e) => setCustomPromptInput(e.target.value)}
                placeholder={t('learningAiModalPh', 'مثلاً:\n• این سند را به زبان آلمانی ترجمه کن\n• فرمول‌ها و تعریف متغیرهای صفحه ۲ را به زبان ساده توضیح بده\n• تمام تمرین‌ها را با حل گرامری استخراج کن...')}
                className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setIsCustomModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                {t('learningAiModalCancel', 'انصراف')}
              </button>
              <button
                type="button"
                disabled={!customPromptInput.trim()}
                onClick={() => {
                  handleTriggerAiAction('custom', customPromptInput);
                  setIsCustomModalOpen(false);
                  setCustomPromptInput('');
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              >
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                {t('learningAiModalSubmit', 'تحلیل توسط هوش مصنوعی')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 bg-gray-950/70 backdrop-blur-xs flex items-center justify-center p-3 md:p-6 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden dir-rtl">
            <div className="p-4 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {getIcon()}
                <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate font-display">
                  {t('learningPreviewTitle', 'مطالعه و تحلیل سند: {{name}}', { name: attachment.name })}
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleOpenFileInNewTab}
                  className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {t('learningPreviewOpenTab', 'باز کردن فایل در زبانه جدید')}
                </button>
                <button
                  type="button"
                  onClick={() => triggerFileDownload(attachment.fileUrl, attachment.name, attachment.id, attachment.key)}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t('learningPreviewDownload', 'دانلود نسخه اصلی')}
                </button>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 overflow-auto bg-slate-50 dark:bg-gray-950 flex flex-col items-center justify-start space-y-4 min-h-[300px]">
              {attachment.type === 'video' && (() => {
                const embedUrl = getEmbedVideoUrl(resolvedUrl || attachment.fileUrl);
                const isInstagram = (resolvedUrl || attachment.fileUrl).includes('instagram.com') || (resolvedUrl || attachment.fileUrl).includes('instagr.am');
                if (embedUrl) {
                  return (
                    <div className={`w-full my-auto ${isInstagram ? 'h-[500px] max-w-sm mx-auto' : 'aspect-video max-w-3xl h-[60vh]'}`}>
                      <iframe
                        src={embedUrl}
                        title={attachment.name}
                        className="w-full h-full rounded-xl border-0 shadow-2xl"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
                return (
                  <div className="w-full max-w-3xl my-auto">
                    <video src={resolvedUrl || attachment.fileUrl} controls autoPlay className="w-full rounded-xl shadow-2xl max-h-[70vh]" />
                  </div>
                );
              })()}

              {attachment.type === 'image' && (
                <div className="my-auto text-center space-y-2">
                  <img src={resolvedUrl || attachment.fileUrl} alt={attachment.name} className="max-w-full max-h-[65vh] rounded-xl shadow-lg object-contain mx-auto" />
                  <p className="text-xs text-gray-400">{attachment.name}</p>
                </div>
              )}

              {(attachment.type === 'pdf' || attachment.type === 'ppt' || attachment.type === 'word' || attachment.type === 'other') && (
                <div className="w-full max-w-3xl space-y-4 my-auto">
                  {/* File Metadata & Quick Controls Card */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-indigo-50 dark:bg-indigo-950/60 rounded-xl text-indigo-600 dark:text-indigo-400">
                        {getIcon()}
                      </div>
                      <div>
                        <h5 className="font-bold text-sm text-gray-900 dark:text-white">
                          {attachment.name}
                        </h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('learningFormatSize', 'فرمت: <span class="uppercase font-bold">{{type}}</span> | حجم: {{size}}', { type: attachment.type, size: attachment.fileSize || t('learningSizeUnknown', 'مشخص‌نشده') })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleOpenFileInNewTab}
                        className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <Eye className="w-4 h-4" />
                        {t('learningBtnOpenFile', 'باز کردن فایل')}
                      </button>
                      <button
                        type="button"
                        onClick={() => triggerFileDownload(attachment.fileUrl, attachment.name, attachment.id, attachment.key)}
                        className="px-3.5 py-2 bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <Download className="w-4 h-4" />
                        {t('learningBtnDownload', 'دانلود')}
                      </button>
                    </div>
                  </div>

                  {/* Inline PDF Viewer Frame */}
                  {attachment.type === 'pdf' && (
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden shadow-sm h-[480px] w-full">
                      <iframe
                        src={resolvedUrl || getWorkerFileUrl(attachment.key || attachment.fileId || attachment.id)}
                        title={attachment.name}
                        className="w-full h-full border-0 bg-white"
                      />
                    </div>
                  )}

                  {/* Extracted Text Content Box / Reader */}
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-5 shadow-sm space-y-3">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
                      <h6 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-500" />
                        {t('learningExtractedTextTitle', 'متن استخراج‌شده سند جهت مطالعه و تحلیل هوش مصنوعی:')}
                      </h6>
                      {attachment.extractedText && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(attachment.extractedText || '');
                            alert(t('learningTextCopied', 'متن سند کپی شد.'));
                          }}
                          className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          {t('learningCopyText', 'کپی متن سند')}
                        </button>
                      )}
                    </div>

                    {attachment.extractedText ? (
                      <div className="bg-slate-50 dark:bg-gray-950/80 rounded-xl p-4 text-xs text-gray-800 dark:text-gray-200 font-sans leading-relaxed max-h-[350px] overflow-y-auto whitespace-pre-wrap border border-gray-100 dark:border-gray-800/80 dir-auto">
                        {attachment.extractedText}
                      </div>
                    ) : (
                      <div className="text-center py-6 px-4 bg-slate-50 dark:bg-gray-950/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-800 space-y-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('learningNoExtractedTextMsg', 'محتوای این سند آماده پردازش توسط هوش مصنوعی است. برای مشاهده محتوا یا تحلیل کلاسی، از دکمه‌های زیر استفاده فرمایید.')}
                        </p>
                      </div>
                    )}

                    {/* AI Prompt Trigger Panel inside Reader */}
                    {onAiPrompt && (
                      <div className="pt-2 space-y-2">
                        <p className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                          {t('learningSendAiPrompt', 'ارسال درخواست هوش مصنوعی بر اساس این سند:')}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setIsPreviewOpen(false);
                              handleTriggerAiAction('translate_fa');
                            }}
                            className="p-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-800 transition-colors cursor-pointer text-center"
                          >
                            🇮🇷 {t('learningAiTransFa', 'ترجمه به فارسی')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsPreviewOpen(false);
                              handleTriggerAiAction('translate_sv');
                            }}
                            className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 rounded-xl text-xs font-bold border border-purple-200 dark:border-purple-800 transition-colors cursor-pointer text-center"
                          >
                            🇸🇪 {t('learningAiTransSv', 'ترجمه به سوئدی')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsPreviewOpen(false);
                              handleTriggerAiAction('summarize');
                            }}
                            className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-xl text-xs font-bold border border-emerald-200 dark:border-emerald-800 transition-colors cursor-pointer text-center"
                          >
                            📝 {t('learningAiSummarizeDesc', 'خلاصه‌سازی نکات')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsPreviewOpen(false);
                              handleTriggerAiAction('quiz');
                            }}
                            className="p-2 bg-amber-50 hover:bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-xl text-xs font-bold border border-amber-200 dark:border-amber-800 transition-colors cursor-pointer text-center"
                          >
                            ✍️ {t('learningAiQuiz', 'طراحی کوییز')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to render inline markdown (bold & inline code)
function parseInlineMarkdown(text: string) {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*|`.*?`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={idx} className="font-extrabold text-indigo-700 dark:text-indigo-300">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={idx} className="bg-indigo-50 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-mono text-xs px-1.5 py-0.5 rounded border border-indigo-100 dark:border-gray-700 mx-0.5">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

// Human-readable AI markdown renderer (converts raw markdown pipe tables into styled tables/cards)
function AiFormattedRenderer({ content, forceLtr }: { content: string; forceLtr: boolean }) {
  const { t } = useLanguage();
  if (!content) return null;

  const lines = content.split('\n');
  const blocks: Array<{ type: 'heading' | 'quote' | 'table' | 'list' | 'paragraph'; content: any }> = [];

  let currentTableLines: string[] = [];
  let currentListItems: string[] = [];

  const flushTable = () => {
    if (currentTableLines.length > 0) {
      const rows = currentTableLines
        .map(line => line.trim())
        .filter(line => line.startsWith('|') && line.endsWith('|'))
        .map(line => 
          line
            .slice(1, -1)
            .split('|')
            .map(cell => cell.trim())
        );

      const cleanRows = rows.filter(row => !row.every(cell => /^:?-+:?$/.test(cell)));

      if (cleanRows.length > 0) {
        const headers = cleanRows[0];
        const bodyRows = cleanRows.slice(1);
        blocks.push({
          type: 'table',
          content: { headers, bodyRows }
        });
      }
      currentTableLines = [];
    }
  };

  const flushList = () => {
    if (currentListItems.length > 0) {
      blocks.push({
        type: 'list',
        content: [...currentListItems]
      });
      currentListItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushList();
      currentTableLines.push(trimmed);
      continue;
    } else {
      flushTable();
    }

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || /^\d+\.\s/.test(trimmed)) {
      currentListItems.push(trimmed.replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, ''));
      continue;
    } else {
      flushList();
    }

    if (!trimmed) continue;

    if (trimmed.startsWith('#')) {
      const level = (trimmed.match(/^#+/) || ['#'])[0].length;
      const text = trimmed.replace(/^#+\s*/, '');
      blocks.push({ type: 'heading', content: { level, text } });
    } else if (trimmed.startsWith('>')) {
      blocks.push({ type: 'quote', content: trimmed.replace(/^>\s*/, '') });
    } else {
      blocks.push({ type: 'paragraph', content: trimmed });
    }
  }

  flushTable();
  flushList();

  return (
    <div dir={forceLtr ? 'ltr' : 'rtl'} className={`space-y-4 text-xs md:text-sm leading-relaxed ${forceLtr ? 'text-left' : 'text-right'}`}>
      {blocks.map((block, bIdx) => {
        if (block.type === 'heading') {
          return (
            <h4 key={bIdx} className="text-sm md:text-base font-extrabold text-indigo-600 dark:text-indigo-400 pt-3 pb-1 border-b border-indigo-100 dark:border-gray-800 flex items-center gap-2 font-display">
              <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
              {parseInlineMarkdown(block.content.text)}
            </h4>
          );
        }

        if (block.type === 'quote') {
          return (
            <div key={bIdx} className="p-3.5 bg-indigo-50/70 dark:bg-indigo-950/30 border-r-4 border-indigo-500 rounded-xl text-xs text-indigo-900 dark:text-indigo-200 space-y-1 font-sans my-2 shadow-xs">
              <div className="font-bold flex items-center gap-1.5 text-indigo-700 dark:text-indigo-300">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <span>{t('learningAiKeyPoint', 'نکته کلیدی:')}</span>
              </div>
              <p className="leading-relaxed">{parseInlineMarkdown(block.content)}</p>
            </div>
          );
        }

        if (block.type === 'list') {
          return (
            <ul key={bIdx} className="space-y-2 my-2 bg-slate-50 dark:bg-gray-800/40 p-3.5 rounded-xl border border-slate-100 dark:border-gray-800">
              {block.content.map((item: string, iIdx: number) => (
                <li key={iIdx} className="flex items-start gap-2 text-xs md:text-sm text-gray-800 dark:text-gray-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0" />
                  <span className="leading-relaxed">{parseInlineMarkdown(item)}</span>
                </li>
              ))}
            </ul>
          );
        }

        if (block.type === 'table') {
          const { headers, bodyRows } = block.content;
          return (
            <div key={bIdx} className="my-4 overflow-hidden rounded-xl border border-indigo-100 dark:border-gray-800 shadow-xs bg-white dark:bg-gray-900">
              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs border-collapse">
                  <thead>
                    <tr className="bg-indigo-600 text-white dark:bg-indigo-950 dark:text-indigo-200">
                      {headers.map((h: string, hIdx: number) => (
                        <th key={hIdx} className="p-3 font-bold border-b border-indigo-700 dark:border-indigo-900 whitespace-nowrap">
                          {parseInlineMarkdown(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {bodyRows.map((row: string[], rIdx: number) => (
                      <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-slate-50/60 dark:bg-gray-800/40'}>
                        {row.map((cell: string, cIdx: number) => (
                          <td key={cIdx} className="p-3 text-gray-800 dark:text-gray-200 leading-relaxed min-w-[120px]">
                            {parseInlineMarkdown(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }

        return (
          <p key={bIdx} className="text-gray-700 dark:text-gray-200 leading-relaxed font-sans">
            {parseInlineMarkdown(block.content)}
          </p>
        );
      })}
    </div>
  );
}

// Helper function to build dynamic, subject-independent educational prompts for AI tutor
function buildComprehensiveLearningPrompt({
  topicTitle,
  topicDescription,
  sessionTitle,
  notesOrRequest,
  historyContext,
  isFollowUp = false
}: {
  topicTitle: string;
  topicDescription?: string;
  sessionTitle: string;
  notesOrRequest: string;
  historyContext?: string;
  isFollowUp?: boolean;
}): string {
  const courseContext = `[نام دوره آموزشی]: "${topicTitle}"${topicDescription ? `\n[توضیحات و اهداف دوره]: "${topicDescription}"` : ''}`;
  const lessonContext = `[عنوان جلسه/درس]: "${sessionTitle}"`;

  // Complete text portions preserved without artificial length limits
  const safeNotes = notesOrRequest || '';
  const safeHistory = historyContext || '';


 const isExplicitNotesRequest =
  /جزوه|خلاصه|سند|درس‌نامه|خلاصه‌سازی|درسنامه|جدول|فایل آموزشی|\bnotes\b|\bsummary\b|\bsummarize\b|lesson plan|study plan|study guide|report|outline|complete notes/i.test(safeNotes);

console.log("Learning prompt debug:", {
  safeNotes,
  isExplicitNotesRequest,
  isFollowUp
});


// ================== NOTES MODE ==================
if (isExplicitNotesRequest) {
  return `شما یک مدرس متخصص هستید و باید برای دانش‌جو یک جزوه آموزشی کامل تهیه کنید.

${courseContext}

${lessonContext}

درخواست دانش‌جو:
${safeNotes}

================== قوانین تهیه جزوه ==================

۱. یک جزوه آموزشی ساختاریافته و کامل تهیه کنید.
۲. موضوع را از سطح مبتدی شروع کنید و مرحله‌به‌مرحله توضیح دهید.
۳. از عنوان‌ها و بخش‌بندی مناسب استفاده کنید.
۴. مفاهیم اصلی، اصطلاحات مهم، مثال‌های واقعی و کاربردهای عملی را توضیح دهید.
۵. اگر موضوع فنی است، در صورت نیاز دستورات، کدها یا مراحل عملی مرتبط را اضافه کنید.
۶. از مثال‌های ساده برای قابل فهم شدن موضوع استفاده کنید.
۷. اطلاعات نادرست یا حدس‌های بدون پایه ایجاد نکنید.
۸. پاسخ باید مانند یک جزوه آموزشی واقعی باشد، نه یک پاسخ کوتاه گفتگو.
۹. از عنوان "تحلیل و جزوه هوش مصنوعی" استفاده نکنید.
۱۰. پاسخ را به زبان درخواست دانش‌جو ارائه دهید.

هدف:
ایجاد یک منبع آموزشی قابل مطالعه که دانش‌جو بتواند با آن یاد بگیرد.
`;
}


// ================== TUTOR MODE ==================
if (isFollowUp) {
  return `شما یک معلم خصوصی هوش مصنوعی و مدرس متخصص هستید.

وظیفه شما کمک به دانش‌جو برای فهم واقعی موضوع است، نه تولید جزوه یا گزارش آموزشی.

${courseContext}

${lessonContext}

${safeHistory ? `گفتگوهای قبلی:\n${safeHistory}\n` : ''}

سؤال دانش‌جو:
${safeNotes}

================== قوانین رفتار معلم ==================

۱. ابتدا دقیقاً به سؤال دانش‌جو پاسخ دهید.
۲. توضیح را با ساده‌ترین و قابل فهم‌ترین روش شروع کنید.
۳. فقط در صورت مفید بودن از مثال، تشبیه یا سناریوی واقعی استفاده کنید.
۴. سطح توضیح را با توجه به سؤال تنظیم کنید:
   - سؤال ساده → پاسخ کوتاه و روشن.
   - سؤال پیچیده → توضیح عمیق‌تر و مرحله‌به‌مرحله.
۵. اگر دانش‌جو دچار سوءتفاهم است، آن را با احترام اصلاح کنید.
۶. از اطلاعات دوره فقط زمانی استفاده کنید که به پاسخ کمک می‌کند.
۷. اطلاعات دوره یا مقدمه‌های غیرضروری را تکرار نکنید.
۸. جزوه، گزارش، فصل‌بندی یا خلاصه طولانی تولید نکنید مگر اینکه دانش‌جو درخواست کند.
۹. به صورت خودکار تمرین، نکات کلیدی یا جمع‌بندی اضافه نکنید.
۱۰. از عنوان "تحلیل و جزوه هوش مصنوعی" استفاده نکنید.
۱۱. پاسخ را به زبان سؤال دانش‌جو ارائه دهید.
۱۲. از مقدمه‌های تکراری مانند "سلام" یا "برای شروع درس..." خودداری کنید.
۱۳. فقط زمانی سؤال پیگیری بپرسید که واقعاً به یادگیری کمک کند.
۱۴. اگر موضوع شامل مقایسه دو مفهوم است، ابتدا تفاوت اصلی را در یک جمله بیان کنید.

هدف:
کمک کنید دانش‌جو مفهوم را واقعاً بفهمد و بتواند از آن استفاده کند.
`;
}


// ================== LESSON EXPLANATION MODE ==================
return `شما یک معلم خصوصی هوش مصنوعی و مدرس متخصص برای دوره آموزشی زیر هستید.

${courseContext}

${lessonContext}

محتوای درس:
${safeNotes}

================== قوانین تدریس ==================

۱. مفاهیم اصلی را شفاف و روان توضیح دهید.
۲. ابتدا مهم‌ترین نکته را بیان کنید و سپس جزئیات لازم را اضافه کنید.
۳. برای بهتر شدن درک دانش‌جو از مثال استفاده کنید.
۴. اصطلاحات مهم را فقط زمانی توضیح دهید که لازم هستند.
۵. اگر اطلاعات کافی نیست، آن را مشخص کنید و حدس نزنید.
۶. بدون درخواست دانش‌جو جزوه کامل یا گزارش ایجاد نکنید.
۷. پاسخ را طبیعی و شبیه یک مدرس انسانی ارائه دهید.
۸. از عنوان "تحلیل و جزوه هوش مصنوعی" استفاده نکنید.
۹. پاسخ را به زبان سؤال دانش‌جو ارائه دهید.

هدف:
دانش‌جو باید موضوع را بفهمد، نه فقط یک متن اطلاعاتی دریافت کند.
`;
}

export default function LearningModule() {
  const { t, lang } = useLanguage();
  const [topics, setTopics] = useState<LearningTopic[]>([]);
  const [sessions, setSessions] = useState<LearningSession[]>([]);
  
  // Navigation View State: 'topics' | 'detail' | 'new_session' | 'session_view' | 'file_manager'
  const [activeView, setActiveView] = useState<'topics' | 'detail' | 'new_session' | 'session_view' | 'file_manager'>('topics');
  
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modal / Form States for Topics
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<LearningTopic | null>(null);
  const [topicTitleInput, setTopicTitleInput] = useState('');
  const [topicDescInput, setTopicDescInput] = useState('');

  // Form States for New Session
  const [sessionTitleInput, setSessionTitleInput] = useState('');
  const [teacherNotesInput, setTeacherNotesInput] = useState('');
  
  // Sub-topics inside a single session
  const [subTopicsInput, setSubTopicsInput] = useState<Array<{ id: string; title: string; notes: string }>>([
    { id: 'st_1', title: '', notes: '' }
  ]);

  const [loadingAi, setLoadingAi] = useState(false);
  const loadingAiRef = useRef(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Interactive follow-up input state inside active session
  const [followUpInput, setFollowUpInput] = useState('');
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const loadingFollowUpRef = useRef(false);
  const [followUpError, setFollowUpError] = useState<string | null>(null);

  // UI state for copied text feedback & LTR toggle in session view
  const [isCopied, setIsCopied] = useState(false);
  const [forceLtr, setForceLtr] = useState(false);

  // Pending attachments state for new session form
  const [pendingAttachments, setPendingAttachments] = useState<SessionAttachment[]>([]);

  // Video Link Sharing Modal State
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const [videoTitleInput, setVideoTitleInput] = useState('');

  // File upload progress state
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');

  // Delete Confirmation Modal State
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    type: 'topic' | 'session' | 'message' | 'attachment';
    id: string;
    title: string;
    parentId?: string;
  }>({
    isOpen: false,
    type: 'session',
    id: '',
    title: '',
  });

  // File processor helper (uploads file to Cloudflare R2 worker + server/api, extracts text for AI context)
  const handleProcessFiles = async (files: FileList | File[] | null, callback: (att: SessionAttachment) => void, sessionId?: string) => {
    if (!files || files.length === 0) return;
    setIsUploadingFile(true);

    const fileList = Array.isArray(files) ? files : Array.from(files);
    setUploadingFileName(fileList.map(f => f.name).join(', '));

    try {
      for (const file of fileList) {
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        
        // Strict Size Guard: 50MB Canonical Limit
        if (file.size > 50 * 1024 * 1024) {
          alert(t('learningFileSizeLimit', 'حجم فایل "{{name}}" بیشتر از سقف مجاز (۵۰ مگابایت) است (FILE_TOO_LARGE).', { name: file.name }));
          continue;
        }

        // Strict Video File Upload Guard
        if (file.type.startsWith('video/') || ['mp4', 'mov', 'webm', 'mkv', 'avi', 'flv', 'wmv'].includes(ext)) {
          alert(t('learningFileVideoAlert', 'آپلود مستقیم فایل ویدیو به علت حجم بالا مجاز نیست.\nلطفاً از دکمه «اشتراک‌گذاری لینک ویدیو» برای قرار دادن لینک ویدیو (یوتیوب، آپارات، اینستاگرام، ویمو یا لینک مستقیم) استفاده فرمایید.'));
          continue;
        }

        let type: SessionAttachment['type'] = 'other';
        if (['ppt', 'pptx'].includes(ext) || file.type.includes('presentation')) type = 'ppt';
        else if (['doc', 'docx'].includes(ext) || file.type.includes('word')) type = 'word';
        else if (ext === 'pdf' || file.type.includes('pdf')) type = 'pdf';
        else if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic', 'heif'].includes(ext)) type = 'image';

        const sizeStr = file.size > 1024 * 1024 
          ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` 
          : `${Math.round(file.size / 1024)} KB`;

        const attId = `att_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

        try {
          // Fire canonical upload immediately with module='learning' and parentId=sessionId
          const uploadPromise = uploadFileToWorker(file, 'learning', sessionId);

          // Extract text in parallel with a non-blocking 3s timeout to prevent PDF/OCR parsing from delaying upload
          const extractPromise = Promise.race([
            extractTextFromFile(file).catch(() => ''),
            new Promise<string>((resolve) => setTimeout(() => resolve(''), 3000))
          ]);

          const [workerRes, extractedText] = await Promise.all([uploadPromise, extractPromise]);

          let r2Key = '';
          let r2FileId = '';
          let r2FileUrl = '';

          if (workerRes && workerRes.success) {
            r2Key = workerRes.key || '';
            r2FileId = workerRes.fileId || '';
            r2FileUrl = workerRes.fileUrl || (r2FileId ? `/api/files/${r2FileId}/download` : getWorkerFileUrl(r2Key));
          } else {
            throw new Error(t('learningFileR2Error', 'آپلود فایل به سیستم ذخیره‌سازی ابری R2 انجام نشد.'));
          }

          const newAtt: SessionAttachment = {
            id: attId,
            fileId: r2FileId || attId,
            key: r2Key,
            name: file.name,
            size: file.size,
            type,
            fileUrl: r2FileUrl,
            fileSize: sizeStr,
            uploadDate: new Date().toISOString(),
            extractedText
          };
          callback(newAtt);
        } catch (err: any) {
          console.error('[Upload Failed]:', err);
          alert(t('learningFileUploadError', 'خطا در آپلود فایل "{{name}}": {{message}}', { name: file.name, message: err.message || t('learningUnknownError', 'مشکل غیرمنتظره') }));
        }
      }
    } finally {
      setIsUploadingFile(false);
      setUploadingFileName('');
    }
  };

  // Add Video Link Attachment Handler
  const handleAddVideoLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rawUrl = videoUrlInput.trim();
    if (!rawUrl) return;

    if (!isSafeVideoUrl(rawUrl)) {
      alert(t('learningVideoInvalidUrl', 'آدرس وارد شده معتبر نیست یا پروتکل امن HTTPS ندارد.'));
      return;
    }

    const meta = parseVideoMetadata(rawUrl);
    const resolvedTitle = videoTitleInput.trim() || meta?.suggestedTitle || t('learningVideoOnlineDefault', 'ویدیو آموزشی آنلاین');

    const newAtt: SessionAttachment = {
      id: `att_vid_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: resolvedTitle,
      type: 'video',
      fileUrl: rawUrl,
      fileSize: meta ? meta.providerName : t('learningVideoOnlineLink', 'لینک آنلاین'),
      uploadDate: new Date().toISOString(),
      provider: meta?.provider,
      videoId: meta?.videoId,
      embedUrl: meta?.embedUrl,
      playbackMode: meta?.playbackMode,
      isExternalLink: true
    };

    if (activeView === 'session_view' && currentSession) {
      addAttachmentToSession(currentSession.id, newAtt).then(updated => {
        if (updated) {
          setSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
        }
      });
    } else {
      setPendingAttachments(prev => [...prev, newAtt]);
    }

    setVideoUrlInput('');
    setVideoTitleInput('');
    setIsVideoModalOpen(false);
  };

  // Load initial data and subscribe to real-time cloud updates across devices
  useEffect(() => {
    refreshData();
    syncLearningDataFromCloud().then(() => {
      refreshData();
    });

    const unsubscribe = subscribeToLearningData(() => {
      refreshData();
    });

    return () => unsubscribe();
  }, []);

  const refreshData = () => {
    setTopics(getLearningTopics());
    setSessions(getLearningSessions());
  };

  // Currently selected topic and session objects
  const currentSession = sessions.find(s => s.id === selectedSessionId) || null;
  const currentTopic = topics.find(t => t.id === selectedTopicId) || (currentSession ? topics.find(t => t.id === currentSession.topicId) : null) || null;

  // Filtered topics based on search query
  const filteredTopics = topics.filter(t => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const topicMatch = t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    const sessionMatch = sessions.some(s => s.topicId === t.id && (
      s.sessionTitle.toLowerCase().includes(q) || 
      s.teacherInput.toLowerCase().includes(q) ||
      s.aiNotes.toLowerCase().includes(q)
    ));
    return topicMatch || sessionMatch;
  });

  // Filtered sessions for selected topic
  const topicSessions = sessions.filter(s => s.topicId === selectedTopicId);

  // Filtered sessions inside topic detail
  const filteredTopicSessions = topicSessions.filter(s => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return s.sessionTitle.toLowerCase().includes(q) || 
           s.teacherInput.toLowerCase().includes(q) || 
           s.aiNotes.toLowerCase().includes(q);
  });

  // Handlers for Topic Modal
  const handleOpenAddTopic = () => {
    setEditingTopic(null);
    setTopicTitleInput('');
    setTopicDescInput('');
    setIsTopicModalOpen(true);
  };

  const handleOpenEditTopic = (topic: LearningTopic, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTopic(topic);
    setTopicTitleInput(topic.title);
    setTopicDescInput(topic.description);
    setIsTopicModalOpen(true);
  };

  const handleSaveTopic = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicTitleInput.trim()) return;

    if (editingTopic) {
      updateTopic(editingTopic.id, topicTitleInput, topicDescInput);
    } else {
      createTopic(topicTitleInput, topicDescInput);
    }
    refreshData();
    setIsTopicModalOpen(false);
    setTopicTitleInput('');
    setTopicDescInput('');
    setEditingTopic(null);
  };

  const handleDeleteTopicConfirm = (topicId: string, topicTitle: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteModalState({
      isOpen: true,
      type: 'topic',
      id: topicId,
      title: topicTitle,
    });
  };

  // Sub-topics management helpers
  const handleAddSubTopic = () => {
    setSubTopicsInput(prev => [
      ...prev,
      { id: `st_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`, title: '', notes: '' }
    ]);
  };

  const handleRemoveSubTopic = (id: string) => {
    if (subTopicsInput.length <= 1) return;
    setSubTopicsInput(prev => prev.filter(st => st.id !== id));
  };

  const handleSubTopicChange = (id: string, field: 'title' | 'notes', value: string) => {
    setSubTopicsInput(prev => prev.map(st => st.id === id ? { ...st, [field]: value } : st));
  };

  // Handler for creating a new session with AI request
  const handleStartNewSessionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTopic || loadingAiRef.current || loadingAi) return;
    if (isUploadingFile) {
      alert(t('learningSessionUploadWait', 'لطفاً تا پایان آپلود کامل فایل شکیبا باشید.'));
      return;
    }
    
    // Check validity
    const validSubTopics = subTopicsInput.filter(st => st.title.trim() || st.notes.trim());
    if (!sessionTitleInput.trim()) {
      setAiError(t('learningSessionTitleReq', 'لطفاً عنوان اصلی جلسه را وارد نمایید.'));
      return;
    }

    if (!teacherNotesInput.trim() && validSubTopics.length === 0 && pendingAttachments.length === 0) {
      setAiError(t('learningSessionMinReq', 'لطفاً حداقل یک مبحث، توضیح یا فایل پیوست برای این جلسه وارد نمایید.'));
      return;
    }

    loadingAiRef.current = true;
    setLoadingAi(true);
    setAiError(null);

    try {
      // Format sub-topics string
      const subTopicsFormattedText = validSubTopics
        .map((st, idx) => t('learningSubtopicFmt', 'مبحث/موضوع {{idx}}: {{title}}\nنکات: {{notes}}', { idx: idx + 1, title: st.title.trim(), notes: st.notes.trim() }))
        .join('\n\n');

      const attachmentNotes = pendingAttachments
        .map(att => t('learningAttNotesFmt', 'پیوست: {{name}}', { name: att.name }) + (att.extractedText ? `\n${t('learningExtractedLabel', 'متن استخراج‌شده سند:')}\n${att.extractedText}` : ''))
        .join('\n\n');

      const combinedNotes = [
        teacherNotesInput.trim() ? `${t('learningOverallDesc', 'توضیحات کلی جلسه:')}\n${teacherNotesInput.trim()}` : '',
        subTopicsFormattedText ? `${t('learningTopicsDiscussed', 'مباحث مطرح‌شده در این جلسه:')}\n${subTopicsFormattedText}` : '',
        attachmentNotes ? `${t('learningAttachedDocsHeader', 'فایل‌ها و اسناد پیوست شده به جلسه:')}\n${attachmentNotes}` : ''
      ].filter(Boolean).join('\n\n');

      const aiMessage = buildComprehensiveLearningPrompt({
        topicTitle: currentTopic.title,
        topicDescription: currentTopic.description,
        sessionTitle: sessionTitleInput.trim(),
        notesOrRequest: combinedNotes,
        isFollowUp: false
      });

      let aiRes: WorkerAiFullResponse | null = null;
      try {
        aiRes = await sendWorkerAiFullRequest('learning_assistant', aiMessage, []);
      } catch (err: any) {
        console.warn('AI Request fallback triggered:', err);
      }

      const aiReplyText = aiRes?.reply || t('learningSessionAiDefaultReply', '### 📌 تحلیل مباحث و جزوه آموزشی جلسه: {{title}}\n\n{{notes}}\n\n---\n*جزوه اولیه ثبت شد. می‌توانید سؤالات بعدی خود را در بخش چت جلسه بپرسید.*', { title: sessionTitleInput.trim(), notes: combinedNotes });

      const newSession = await createSession(
        currentTopic.id,
        sessionTitleInput.trim(),
        combinedNotes,
        aiReplyText,
        '',
        '',
        validSubTopics,
        pendingAttachments,
        (aiRes && (aiRes.debug?.model || aiRes.model)) ? {
          provider: aiRes.debug?.provider || aiRes.provider || undefined,
          model: aiRes.debug?.model || aiRes.model || undefined,
          responseTime: aiRes.debug?.responseTime
        } : undefined
      );

      refreshData();
      setSelectedSessionId(newSession.id);
      setActiveView('session_view');

      setSessionTitleInput('');
      setTeacherNotesInput('');
      setSubTopicsInput([{ id: 'st_1', title: '', notes: '' }]);
      setPendingAttachments([]);
    } finally {
      setLoadingAi(false);
      loadingAiRef.current = false;
    }
  };

  // Follow-up AI handler inside active session with custom text prompt
  const handleSendFollowUpAiWithText = async (promptText: string) => {
    if (!promptText.trim() || !currentSession || !currentTopic || loadingFollowUpRef.current || loadingFollowUp) return;

    loadingFollowUpRef.current = true;
    setLoadingFollowUp(true);
    setFollowUpError(null);

    const sessionMessages: LearningSessionMessage[] = Array.isArray(currentSession.messages)
      ? currentSession.messages
      : [
          ...(currentSession.teacherInput ? [{
            id: 'msg_init_user',
            sender: 'user' as const,
            text: currentSession.teacherInput,
            timestamp: currentSession.createdDate
          }] : []),
          ...(currentSession.aiNotes ? [{
            id: 'msg_init_ai',
            sender: 'ai' as const,
            text: currentSession.aiNotes,
            timestamp: currentSession.createdDate
          }] : [])
        ];

    const historyText = sessionMessages.map(m => {
      if (m.sender === 'user') return `User Question: ${m.text}`;
      if (m.sender === 'teacher_note') return `Pasted Teacher Note: ${m.text}`;
      return `AI Response: ${m.text}`;
    }).join('\n\n---\n\n');

    const fullPrompt = buildComprehensiveLearningPrompt({
      topicTitle: currentTopic.title,
      topicDescription: currentTopic.description,
      sessionTitle: currentSession.sessionTitle,
      notesOrRequest: promptText.trim(),
      historyContext: historyText,
      isFollowUp: true
    });

    try {
      const aiRes = await sendWorkerAiFullRequest('learning_assistant', fullPrompt, []);
      if (!aiRes || !aiRes.reply) {
        throw new Error(t('learningAiNoResponse', 'پاسخی دریافت نشد.'));
      }

      addMessageToSession(currentSession.id, { sender: 'user', text: promptText.trim() });
      addMessageToSession(currentSession.id, {
        sender: 'ai',
        text: aiRes.reply,
        aiInfo: (aiRes.debug?.model || aiRes.model) ? {
          provider: aiRes.debug?.provider || aiRes.provider || undefined,
          model: aiRes.debug?.model || aiRes.model || undefined,
          responseTime: aiRes.debug?.responseTime
        } : undefined
      });

      refreshData();
      setFollowUpInput('');
    } catch (err: any) {
      console.warn('Follow-up AI request fallback triggered:', err);
      // Graceful fallback like NutritionModule — save user prompt & add structured note so content is never lost
      const fallbackAiReply = t('learningAiFallbackReply', '### 📌 تحلیل و خلاصه‌سازی هوشمند:\n\n{{text}}\n\n---\n*نکته: پاسخ هوش مصنوعی در حال حاضر به‌صورت خلاصه ثبت شد. می‌توانید سؤال خود را مجدداً ارسال نمایید.*', { text: promptText.trim() });

      addMessageToSession(currentSession.id, { sender: 'user', text: promptText.trim() });
      addMessageToSession(currentSession.id, { sender: 'ai', text: fallbackAiReply });

      refreshData();
      setFollowUpInput('');
    } finally {
      setLoadingFollowUp(false);
      loadingFollowUpRef.current = false;
    }
  };

  const handleSendFollowUpAi = async (e: React.FormEvent) => {
    e.preventDefault();
    handleSendFollowUpAiWithText(followUpInput);
  };

  // Direct Teacher Note Handler (Paste without calling AI)
  const handleAddDirectTeacherNote = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!followUpInput.trim() || !currentSession) return;

    addMessageToSession(currentSession.id, { sender: 'teacher_note', text: followUpInput.trim() });
    refreshData();
    setFollowUpInput('');
  };

  const handleDeleteSessionConfirm = (sessionId: string, sessionTitle: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDeleteModalState({
      isOpen: true,
      type: 'session',
      id: sessionId,
      title: sessionTitle,
    });
  };

  const handleDeleteMessageConfirm = (messageId: string, label: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!currentSession) return;
    setDeleteModalState({
      isOpen: true,
      type: 'message',
      id: messageId,
      title: label,
      parentId: currentSession.id,
    });
  };

  const handleDeleteAttachmentConfirm = (attachmentId: string, name: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!currentSession) return;
    setDeleteModalState({
      isOpen: true,
      type: 'attachment',
      id: attachmentId,
      title: name,
      parentId: currentSession.id,
    });
  };

  const handleConfirmDelete = () => {
    const { type, id, parentId } = deleteModalState;
    setDeleteModalState(prev => ({ ...prev, isOpen: false }));

    if (type === 'topic') {
      deleteTopic(id);
      refreshData();
      if (selectedTopicId === id) {
        setSelectedTopicId(null);
        setSelectedSessionId(null);
        setActiveView('topics');
      }
    } else if (type === 'session') {
      deleteSession(id);
      refreshData();
      if (selectedSessionId === id) {
        setSelectedSessionId(null);
        if (selectedTopicId) {
          setActiveView('detail');
        } else {
          setActiveView('topics');
        }
      }
    } else if (type === 'message') {
      const sessId = parentId || currentSession?.id;
      if (sessId) {
        deleteMessageFromSession(sessId, id);
        refreshData();
      }
    } else if (type === 'attachment') {
      const sessId = parentId || currentSession?.id;
      if (sessId) {
        const targetAtt = currentSession?.attachments?.find(a => a.id === id);
        const attKey = targetAtt?.key || targetAtt?.fileId || id;
        const attId = targetAtt?.id || id;

        // 1. Immediately update local React state so attachment disappears instantly from UI
        setSessions(prevSessions => prevSessions.map(s => {
          if (s.id === sessId) {
            return {
              ...s,
              attachments: (s.attachments || []).filter(a =>
                a.id !== id &&
                a.id !== attId &&
                a.id !== targetAtt?.fileId &&
                a.key !== attKey &&
                !(a.fileUrl && a.fileUrl.includes(id)) &&
                !(a.fileUrl && a.fileUrl.includes(attId))
              )
            };
          }
          return s;
        }));

        // 2. Perform single unified backend deletion
        const deleteTargetId = targetAtt?.fileId || attId || id;
        deleteAttachmentFromSession(sessId, deleteTargetId).finally(() => {
          refreshData();
        });
      }
    }
  };

  // Copy AI Notes / Full Thread
  const handleCopyAiNotes = () => {
    if (!currentSession) return;

    const sessionMessages: LearningSessionMessage[] = Array.isArray(currentSession.messages)
      ? currentSession.messages
      : [
          ...(currentSession.teacherInput ? [{ id: '1', sender: 'user' as const, text: currentSession.teacherInput, timestamp: '' }] : []),
          ...(currentSession.aiNotes ? [{ id: '2', sender: 'ai' as const, text: currentSession.aiNotes, timestamp: '' }] : [])
        ];

    const fullText = sessionMessages.map(m => {
      const tag = m.sender === 'user' 
        ? `[${t('learningUserMsgTag', 'پرسش/مبحث کاربر')}]` 
        : m.sender === 'teacher_note' 
        ? `[${t('learningTeacherNoteTag', 'جزوه استاد')}]` 
        : `[${t('learningAiResponseTag', 'تحلیل هوش مصنوعی')}]`;
      return `${tag}:\n${m.text}`;
    }).join('\n\n====================\n\n');

    navigator.clipboard.writeText(t('learningCopyFullThread', 'موضوع: {{topic}}\nجلسه: {{session}}\n\n{{text}}', { topic: currentTopic?.title, session: currentSession.sessionTitle, text: fullText }));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Helper to format date string into Gregorian calendar presentation
  const formatDateLabel = (isoDateStr: string) => {
    if (!isoDateStr) return '';
    return formatGregorianDate(isoDateStr, lang, 'medium');
  };

  return (
    <div className="space-y-6 pb-12 animate-fadeIn">

      {/* Top Header & Navigation Banner */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white rounded-3xl p-6 md:p-7 shadow-xl shadow-indigo-600/15 relative overflow-hidden border border-indigo-400/30">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-56 h-56 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-56 h-56 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-indigo-100 text-xs font-extrabold uppercase tracking-wider mb-1">
              <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
              <span>{t('learningTitle', 'دستیار هوشمند یادگیری')}</span>
            </div>
            <h1 className="text-xl md:text-2xl font-extrabold font-display tracking-tight flex items-center gap-2 text-white">
              <GraduationCap className="w-7 h-7 text-amber-300 shrink-0" />
              {t('learningHeaderTitle', 'دستیار یادگیری و مدیریت منابع هوشمند')}
            </h1>
            <p className="text-xs md:text-sm text-indigo-100 mt-1 max-w-2xl leading-relaxed">
              {t('learningHeaderDesc', 'طراحی جلسات آموزشی گام‌به‌گام برای تمام زبان‌ها، نرم‌افزارها، دروس دانشگاهی و مهارت‌های فردی با پشتیبانی هوش مصنوعی.')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              onClick={() => setActiveView('file_manager')}
              className="bg-white/15 hover:bg-white/25 text-white border border-white/30 px-3.5 py-2.5 rounded-xl text-xs font-extrabold flex items-center justify-center gap-2 transition-all cursor-pointer backdrop-blur-md shadow-sm"
            >
              <Folder className="w-4 h-4 text-amber-300" />
              {t('learningSharedFilesTab', 'مدیریت فایل‌های اشتراکی')}
            </button>

            {activeView === 'topics' && (
              <button
                onClick={handleOpenAddTopic}
                className="w-full md:w-auto bg-amber-400 hover:bg-amber-300 text-slate-950 font-black px-4.5 py-2.5 rounded-xl text-xs shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
              >
                <Plus className="w-4 h-4 text-slate-950 stroke-[3]" />
                {t('learningNewTopic', 'مبحث جدید')}
              </button>
            )}

            {activeView !== 'topics' && (
              <button
                onClick={() => {
                  if (activeView === 'session_view') {
                    setActiveView('detail');
                  } else {
                    setActiveView('topics');
                  }
                }}
                className="bg-white/10 hover:bg-white/20 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer backdrop-blur-xs"
              >
                <ChevronLeft className="w-4 h-4 rotate-180" />
                {t('back', 'بازگشت')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* SEARCH BAR & METRICS - VISIBLE IN TOPICS & DETAIL VIEWS */}
      {(activeView === 'topics' || activeView === 'detail') && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('learningSearchPh', 'جستجو در موضوعات، جلسات، یادداشت‌ها و مطالب آموزشی...')}
              className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl pr-10 pl-4 py-2.5 text-xs text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-xs"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs font-bold"
              >
                {t('learningSearchClear', 'پاک کردن')}
              </button>
            )}
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800/80 rounded-xl p-3.5 flex items-center gap-3 shadow-xs">
            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-gray-400 font-medium block">{t('learningTotalTopics', 'کل موضوعات اصلی')}</span>
              <span className="text-base font-bold text-gray-900 dark:text-white font-display">
                {t('learningTopicsCount', '{{count}} موضوع', { count: formatNumberByLang(topics.length, lang) })}
              </span>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800/80 rounded-xl p-3.5 flex items-center gap-3 shadow-xs">
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-lg">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-gray-400 font-medium block">{t('learningTotalSessions', 'کل جلسات ثبت‌شده')}</span>
              <span className="text-base font-bold text-gray-900 dark:text-white font-display">
                {t('learningSessionsCount', '{{count}} جلسه', { count: formatNumberByLang(sessions.length, lang) })}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 1. MAIN TOPICS SCREEN */}
      {/* ==================================================================== */}
      {activeView === 'topics' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-indigo-500" />
              {t('learningMainTopicsList', 'موضوعات اصلی ثبت‌شده')}
            </h2>
            <span className="text-xs text-gray-400">
              {t('learningTopicsOf', '{{filtered}} از {{total}} موضوع', { filtered: formatNumberByLang(filteredTopics.length, lang), total: formatNumberByLang(topics.length, lang) })}
            </span>
          </div>

          {filteredTopics.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center space-y-3">
              <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-500 rounded-full flex items-center justify-center mx-auto">
                <GraduationCap className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold text-gray-700 dark:text-gray-300">
                {searchQuery ? t('learningNoTopicMatch', 'هیچ موضوعی با عبارات جستجوی شما پیدا نشد.') : t('learningNoTopicsYet', 'هنوز هیچ موضوع اصلی ثبت نکرده‌اید.')}
              </p>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                {t('learningTopicHint', 'شما می‌توانید موضوعاتی مثل "زبان سوئدی"، "آموزش Docker"، "برنامه‌نویسی Python"، "آشپزی" یا هر مبحث دلخواه را اضافه کنید.')}
              </p>
              <button
                onClick={handleOpenAddTopic}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 shadow-md shadow-indigo-500/20 transition-all cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                {t('learningCreateFirstTopic', 'ایجاد اولین موضوع آموزشی')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTopics.map((topic) => {
                const topicSessionList = sessions.filter(s => s.topicId === topic.id);
                return (
                  <div
                    key={topic.id}
                    onClick={() => {
                      setSelectedTopicId(topic.id);
                      setActiveView('detail');
                    }}
                    className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800/80 hover:border-indigo-300 dark:hover:border-indigo-700/60 rounded-2xl p-5 shadow-xs hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between relative overflow-hidden"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-base font-display shrink-0 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                            {topic.title.substring(0, 1)}
                          </div>
                          <div>
                            <h3 className="font-bold text-sm text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {topic.title}
                            </h3>
                            <span className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                              <Calendar className="w-3 h-3" />
                              {t('learningTopicCreated', 'ایجاد: {{date}}', { date: formatDateLabel(topic.createdDate) })}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={(e) => handleOpenEditTopic(topic, e)}
                            title={t('learningEditTopic', 'ویرایش موضوع')}
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg transition-colors cursor-pointer"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteTopicConfirm(topic.id, topic.title, e)}
                            title={t('learningDeleteTopic', 'حذف موضوع')}
                            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
                        {topic.description || t('learningNoExtraDesc', 'بدون توضیحات اضافی')}
                      </p>
                    </div>

                    <div className="pt-4 mt-3 border-t border-gray-100 dark:border-gray-800/60 flex items-center justify-between text-xs">
                      <span className="bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 font-bold px-2.5 py-1 rounded-full text-[11px] flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {t('learningSessionsRegistered', '{{count}} جلسه ثبت‌شده', { count: formatNumberByLang(topicSessionList.length, lang) })}
                      </span>

                      <span className="text-indigo-600 dark:text-indigo-400 font-bold text-[11px] flex items-center gap-1 group-hover:translate-x-[-2px] transition-transform">
                        {t('learningViewSessions', 'مشاهده جلسات')}
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* 2. TOPIC DETAIL SCREEN */}
      {/* ==================================================================== */}
      {activeView === 'detail' && currentTopic && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-5 shadow-xs space-y-3">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs text-indigo-500 font-bold mb-1">
                  <BookOpen className="w-4 h-4" />
                  {t('learningSelectedTopic', 'موضوع آموزشی انتخاب‌شده', { name: currentTopic.title })}
                </div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white font-display">
                  {currentTopic.title}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                  {currentTopic.description || t('learningNoExtraDesc', 'بدون توضیحات اضافی')}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    setSessionTitleInput('');
                    setTeacherNotesInput('');
                    setSubTopicsInput([{ id: 'st_1', title: '', notes: '' }]);
                    setAiError(null);
                    setActiveView('new_session');
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 flex items-center gap-2 transition-all cursor-pointer"
                >
                  <Sparkles className="w-4 h-4 text-emerald-300 animate-pulse" />
                  {t('learningStartNewSession', 'شروع جلسه آموزشی جدید')}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-500" />
              {t('learningTopicHistory', 'تاریخچه جلسات این موضوع')}
            </h3>
            <span className="text-xs text-gray-400">
              {t('learningSessionsCount', '{{count}} جلسه', { count: formatNumberByLang(filteredTopicSessions.length, lang) })}
            </span>
          </div>

          {filteredTopicSessions.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center space-y-3">
              <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-500 rounded-full flex items-center justify-center mx-auto">
                <Layers className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold text-gray-700 dark:text-gray-300">
                {searchQuery ? t('learningNoSessionMatch', 'هیچ جلسه‌ای با مشخصات درخواستی یافت نشد.') : t('learningNoSessionsYet', 'هنوز هیچ جلسه‌ای برای این موضوع ثبت نشده است.')}
              </p>
              <button
                onClick={() => {
                  setSessionTitleInput('');
                  setTeacherNotesInput('');
                  setSubTopicsInput([{ id: 'st_1', title: '', notes: '' }]);
                  setAiError(null);
                  setActiveView('new_session');
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                {t('learningSaveFirstSession', 'ثبت اولین جلسه با هوش مصنوعی')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTopicSessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => {
                    setSelectedTopicId(session.topicId);
                    setSelectedSessionId(session.id);
                    setActiveView('session_view');
                  }}
                  className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800/80 hover:border-indigo-300 dark:hover:border-indigo-700/60 rounded-xl p-4 shadow-xs hover:shadow-md transition-all cursor-pointer group flex flex-col md:flex-row md:items-center justify-between gap-3"
                >
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">
                        {session.sessionTitle}
                      </span>
                      <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full font-mono">
                        {formatDateLabel(session.createdDate)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                      <strong className="text-gray-700 dark:text-gray-300">{t('learningNotesTopicsLabel', 'نکات/مباحث:')}</strong> {session.teacherInput}
                    </p>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-2 shrink-0">
                    <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                      {t('learningViewSessionContinue', 'مشاهده جلسه و ادامه گفتگو')}
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </span>
                    <button
                      onClick={(e) => handleDeleteSessionConfirm(session.id, session.sessionTitle, e)}
                      title={t('learningDeleteSession', 'حذف جلسه')}
                      className="p-1.5 hover:bg-red-50 dark:hover:bg-red-950/30 text-gray-400 hover:text-red-500 rounded-lg transition-colors cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================================================================== */}
      {/* 3. NEW SESSION SCREEN */}
      {/* ==================================================================== */}
      {activeView === 'new_session' && currentTopic && (
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-md space-y-5">
            <div className="border-b border-gray-100 dark:border-gray-800 pb-4">
              <span className="text-xs text-indigo-500 font-bold block mb-1">
                {t('learningMainTopicLabel', 'موضوع اصلی: {{title}}', { title: currentTopic.title })}
              </span>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white font-display flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500" />
                {t('learningStartNewSession', 'شروع جلسه آموزشی جدید')}
              </h2>
                {t('learningNewSessionDesc', 'می‌توانید چندین مبحث یا موضوع مختلف مربوط به این جلسه را اضافه کنید تا هوش مصنوعی یک جزوه کامل بسازد.')}
            </div>

            {aiError && (
              <div className="p-3.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl text-xs text-red-600 dark:text-red-400 flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5 shrink-0" />
                <span>{aiError}</span>
              </div>
            )}

            <form onSubmit={handleStartNewSessionSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('learningSessionMainTitle', 'عنوان کلی جلسه آموزشی')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={sessionTitleInput}
                  onChange={(e) => setSessionTitleInput(e.target.value)}
                  placeholder={t('learningSessionTitlePh', 'مثلاً: جلسه ۲: افعال زمان گذشته، آینده و واژگان جدید')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('learningSessionIntroOpt', 'توضیحات کلی یا مقدمه جلسه (اختیاری)')}
                </label>
                <textarea
                  rows={2}
                  value={teacherNotesInput}
                  onChange={(e) => setTeacherNotesInput(e.target.value)}
                  placeholder={t('learningSessionIntroPh', 'توضیح عمومی درباره آنچه در این جلسه گذشت...')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                />
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <ListPlus className="w-4 h-4 text-indigo-500" />
                    {t('learningSubtopicsList', 'مباحث و موضوعات فرعی این جلسه')}
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSubTopic}
                    className="text-xs bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('learningAddSubtopic', 'افزودن مبحث جدید')}
                  </button>
                </div>

                <div className="space-y-3">
                  {subTopicsInput.map((st, index) => (
                    <div 
                      key={st.id} 
                      className="p-3.5 bg-slate-50 dark:bg-gray-800/60 border border-slate-200 dark:border-gray-700/80 rounded-xl space-y-2.5 relative group"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                          <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[10px] flex items-center justify-center font-mono">
                            {index + 1}
                          </span>
                            {t('learningSubtopicTitleLabel', 'عنوان مبحث / موضوع فرعی')}
                        </span>

                        {subTopicsInput.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveSubTopic(st.id)}
                            className="text-gray-400 hover:text-red-500 p-1 rounded-lg transition-colors cursor-pointer"
                            title={t('learningDeleteSubtopic', 'حذف این مبحث')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* 1. Subtopic Title Input with Dedicated Voice Input Button */}
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={st.title}
                          onChange={(e) => handleSubTopicChange(st.id, 'title', e.target.value)}
                          placeholder={t('learningSubtopicTitlePh', 'عنوان مبحث {{idx}} (مثلاً: افعال آینده / دستور کانتینرهای Docker)', { idx: index + 1 })}
                          className="flex-1 min-w-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <VoiceInputButton
                          currentValue={st.title}
                          onUpdate={(val) => handleSubTopicChange(st.id, 'title', val)}
                          title={t('voiceInputTitleForField', 'ورودی صوتی برای عنوان مبحث')}
                        />
                      </div>

                      {/* 2. Subtopic Notes / Details Textarea with Dedicated Voice Input Button */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                            {t('learningSubtopicsList', 'مباحث و بخش‌های فرعی')} ({t('learningTeacherNoteLabel', 'یادداشت/توضیحات')})
                          </span>
                          <VoiceInputButton
                            currentValue={st.notes}
                            onUpdate={(val) => handleSubTopicChange(st.id, 'notes', val)}
                            title={t('voiceInputTitleForNotes', 'ورودی صوتی برای توضیحات و مباحث فرعی')}
                          />
                        </div>
                        <textarea
                          rows={3}
                          value={st.notes}
                          onChange={(e) => handleSubTopicChange(st.id, 'notes', e.target.value)}
                          placeholder={t('learningSubtopicNotesPh', 'توضیحات و نکات استاد درباره این مبحث...')}
                          className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 leading-relaxed"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* File Attachment Uploader (Video Link, PPT, Word, PDF) */}
              <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                    <Paperclip className="w-4 h-4 text-purple-500" />
                    {t('learningAttachmentsLabel', 'فایل‌ها و فیلم‌های آموزشی این جلسه (پاورپوینت، ورد، PDF، لینک ویدیو)')}
                  </label>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsVideoModalOpen(true)}
                      className="text-xs bg-purple-100 dark:bg-purple-950/80 hover:bg-purple-200 text-purple-800 dark:text-purple-300 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer border border-purple-200 dark:border-purple-800"
                    >
                      <Video className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                      {t('learningShareVideoBtn', 'اشتراک‌گذاری لینک ویدیو')}
                    </button>

                    <label className="relative overflow-hidden text-xs bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer border border-indigo-200 dark:border-indigo-800">
                      <UploadCloud className="w-3.5 h-3.5" />
                      <span>{t('learningAttachDocsBtn', 'پیوست اسناد (PDF/Word/PPT/عکس)')}</span>
                      <input 
                        type="file" 
                        multiple 
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.json,.md,.heic,.heif" 
                        onChange={(e) => {
                          const inputEl = e.target;
                          const files = inputEl.files ? Array.from(inputEl.files) : null;
                          if (files && files.length > 0) {
                            handleProcessFiles(files, (att) => setPendingAttachments(prev => [...prev, att]))
                              .finally(() => { if (inputEl) inputEl.value = ''; });
                          }
                        }}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10" 
                      />
                    </label>
                  </div>
                </div>

                {pendingAttachments.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {pendingAttachments.map(att => (
                      <AttachmentCard 
                        key={att.id} 
                        attachment={att} 
                        onDelete={() => setPendingAttachments(prev => prev.filter(a => a.id !== att.id))} 
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 bg-slate-50 dark:bg-gray-800/40 p-3 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-center">
                    {t('learningAttachmentsHint', 'می‌توانید لینک فیلم‌های آموزشی یوتیوب/آپارات، اسلایدهای پاورپوینت (PPT)، اسناد Word یا PDF معلم خود را اینجا اضافه کنید.')}
                  </p>
                )}
              </div>

              <div className="pt-3 flex items-center justify-end gap-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setActiveView('detail')}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  {t('learningCancel', 'انصراف')}
                </button>

                <button
                  type="submit"
                  disabled={loadingAi || isUploadingFile}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 flex items-center gap-2 cursor-pointer transition-all disabled:opacity-50"
                >
                  {loadingAi ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin text-white" />
                      {t('learningGeneratingNotes', 'در حال ساخت جزوه و خلاصه هوشمند...')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-emerald-300" />
                      {t('learningAnalyzeGenerateBtn', 'تحلیل مباحث جلسه و تولید جزوه آموزشی')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 4. ACTIVE SESSION VIEW SCREEN (Interactive Chat & Note Logging) */}
      {/* ==================================================================== */}
      {activeView === 'session_view' && currentSession && currentTopic && (() => {
        // Prepare list of messages for this session
        const sessionMessages: LearningSessionMessage[] = Array.isArray(currentSession.messages)
          ? currentSession.messages
          : [
              ...(currentSession.teacherInput ? [{
                id: 'msg_init_user',
                sender: 'user' as const,
                text: currentSession.teacherInput,
                timestamp: currentSession.createdDate
              }] : []),
              ...(currentSession.aiNotes ? [{
                id: 'msg_init_ai',
                sender: 'ai' as const,
                text: currentSession.aiNotes,
                timestamp: currentSession.createdDate,
                aiInfo: currentSession.aiInfo
              }] : [])
            ];

        return (
          <div className="space-y-6 max-w-4xl mx-auto">
            {/* Top Session Control Bar */}
            <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-xs text-indigo-500 font-bold mb-1">
                  <span>{currentTopic.title}</span>
                  <span>&bull;</span>
                  <span>{t('learningInteractiveSession', 'جلسه تعاملی و ثبت اطلاعات')}</span>
                </div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white font-display">
                  {currentSession.sessionTitle}
                </h2>
                <span className="text-[11px] text-gray-400 mt-0.5 block">
                  {t('learningSessionStartDate', 'تاریخ شروع: {{date}}', { date: formatDateLabel(currentSession.createdDate) })}
                </span>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <label className="relative overflow-hidden bg-purple-50 dark:bg-purple-950/40 hover:bg-purple-100 text-purple-700 dark:text-purple-300 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer">
                  <UploadCloud className="w-3.5 h-3.5" />
                  <span>{t('learningAddVideoFile', 'افزودن ویدیو / فایل')}</span>
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.json,.md,.heic,.heif" 
                    onChange={(e) => {
                      const inputEl = e.target;
                      const files = inputEl.files ? Array.from(inputEl.files) : null;
                      if (files && files.length > 0) {
                        handleProcessFiles(files, (att) => {
                          addAttachmentToSession(currentSession.id, att);
                          refreshData();
                        }).finally(() => { if (inputEl) inputEl.value = ''; });
                      }
                    }}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10" 
                  />
                </label>

                <button
                  onClick={() => setForceLtr(!forceLtr)}
                  title={t('learningToggleDirection', 'تغییر جهت متن (چپ‌چین / راست‌چین)')}
                  className="bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  {forceLtr ? <AlignRight className="w-3.5 h-3.5" /> : <AlignLeft className="w-3.5 h-3.5" />}
                  <span>{forceLtr ? t('learningDirectionRTL', 'راست‌چین (RTL)') : t('learningDirectionLTR', 'چپ‌چین (LTR)')}</span>
                </button>

                <button
                  onClick={handleCopyAiNotes}
                  className="bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{isCopied ? t('learningCopied', 'کپی شد!') : t('learningCopyFullSession', 'کپی کامل این جلسه')}</span>
                </button>

                <button
                  onClick={(e) => handleDeleteSessionConfirm(currentSession.id, currentSession.sessionTitle, e)}
                  className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-xl transition-colors cursor-pointer"
                  title={t('learningDeleteSession', 'حذف جلسه')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Session File Attachments Gallery (Video, PPT, Word, PDF) */}
            <div className="bg-white dark:bg-gray-900 border border-purple-100 dark:border-purple-950/40 rounded-2xl p-4 shadow-xs space-y-3 animate-fadeIn">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h4 className="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                  <Paperclip className="w-4 h-4 text-purple-500" />
                  {t('learningSessionAttachmentsTitle', 'فایل‌ها و فیلم‌های آموزشی پیوست‌شده این جلسه ({{count}} مورد)', { count: formatNumberByLang((currentSession.attachments || []).length, lang) })}
                </h4>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsVideoModalOpen(true)}
                    className="text-[11px] bg-purple-100 dark:bg-purple-950/80 hover:bg-purple-200 text-purple-800 dark:text-purple-300 font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors cursor-pointer border border-purple-200 dark:border-purple-800"
                  >
                    <Video className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                    {t('learningShareVideoSm', 'اشتراک ویدیو')}
                  </button>

                  <label className="relative overflow-hidden text-[11px] bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 font-bold px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors cursor-pointer border border-indigo-200 dark:border-indigo-800">
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('learningAddDocSm', 'افزودن سند')}</span>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.json,.md,.heic,.heif" 
                      onChange={(e) => {
                        const inputEl = e.target;
                        const files = inputEl.files ? Array.from(inputEl.files) : null;
                        if (files && files.length > 0) {
                          handleProcessFiles(files, (att) => {
                            addAttachmentToSession(currentSession.id, att).then(updated => {
                              if (updated) {
                                setSessions(prev => prev.map(s => s.id === updated.id ? updated : s));
                              }
                            });
                          }, currentSession.id).finally(() => { if (inputEl) inputEl.value = ''; });
                        }
                      }}
                      className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10" 
                    />
                  </label>
                </div>
              </div>

              {currentSession.attachments && currentSession.attachments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {currentSession.attachments.map(att => (
                    <AttachmentCard 
                      key={att.id} 
                      attachment={att} 
                      onDelete={() => handleDeleteAttachmentConfirm(att.id, att.name)} 
                      onAiPrompt={(promptText) => handleSendFollowUpAiWithText(promptText)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-gray-400 italic bg-slate-50 dark:bg-gray-800/40 p-3 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-center">
                  {t('learningNoAttachmentsYet', 'هنوز فایلی به این جلسه پیوست نشده است. می‌توانید با دکمه‌های بالا سند یا لینک ویدیو اضافه کنید.')}
                </p>
              )}
            </div>

            {/* Continuous Conversation / Session Items Feed */}
            <div className="space-y-6">
              {sessionMessages.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center space-y-2">
                  <p className="text-xs font-bold text-gray-500 dark:text-gray-400">
                    {t('learningAllMessagesDeleted', 'تمام ورودی‌ها و پیام‌های این جلسه حذف شده‌اند.')}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {t('learningMsgHint', 'می‌توانید با استفاده از فرم پایین، سوال جدید بپرسید یا یادداشت جدید اضافه کنید.')}
                  </p>
                </div>
              ) : (
                sessionMessages.map((msg, idx) => {
                  const msgId = msg.id || `msg_${idx}`;
                  if (msg.sender === 'user') {
                    return (
                      <div key={msgId} className="bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-2xl p-4 space-y-2 group">
                        <div className="flex items-center justify-between text-[11px] font-bold text-indigo-700 dark:text-indigo-300">
                          <span className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-indigo-500" />
                            {t('learningUserMsgLabel', 'ورودی / مبحث مطرح‌شده توسط کاربر:')}
                          </span>
                          <div className="flex items-center gap-2">
                            {msg.timestamp && (
                              <span className="text-[10px] text-gray-400 font-mono">
                                {formatDateLabel(msg.timestamp)}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteMessageConfirm(msgId, t('learningUserMsgLabel', 'ورودی / مبحث کاربر'), e)}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors cursor-pointer"
                              title={t('learningDeleteUserMsg', 'حذف این ورودی / مبحث')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs md:text-sm text-gray-800 dark:text-gray-200 leading-relaxed font-sans whitespace-pre-wrap">
                          {msg.text}
                        </p>
                      </div>
                    );
                  }

                  if (msg.sender === 'teacher_note') {
                    return (
                      <div key={msgId} className="bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-900/40 rounded-2xl p-4.5 space-y-2 shadow-xs group">
                        <div className="flex items-center justify-between text-[11px] font-bold text-amber-800 dark:text-amber-300">
                          <span className="flex items-center gap-1.5">
                            <StickyNote className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            {t('learningTeacherNoteLabel', 'جُزوه / نکات مستقیم اضافه شده توسط استاد:')}
                          </span>
                          <div className="flex items-center gap-2">
                            {msg.timestamp && (
                              <span className="text-[10px] text-amber-600/70 dark:text-amber-400/70 font-mono">
                                {formatDateLabel(msg.timestamp)}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => handleDeleteMessageConfirm(msgId, t('learningTeacherNote', 'یادداشت استاد'), e)}
                              className="p-1 text-amber-600/60 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors cursor-pointer"
                              title={t('learningDeleteTeacherNote', 'حذف این یادداشت استاد')}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs md:text-sm text-amber-950 dark:text-amber-100 leading-relaxed font-sans whitespace-pre-wrap pt-1">
                          {msg.text}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msgId} className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-5 md:p-6 shadow-sm space-y-4 group">
                      <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-indigo-500" />
                          <h3 className="text-xs md:text-sm font-bold text-gray-900 dark:text-white font-display">
                            {t('learningAiNoteTitle', 'تحلیل و جزوه هوش مصنوعی')}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {msg.timestamp && (
                            <span className="text-[10px] text-gray-400 font-mono">
                              {formatDateLabel(msg.timestamp)}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => handleDeleteMessageConfirm(msgId, t('learningAiResponse', 'پاسخ هوش مصنوعی'), e)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors cursor-pointer"
                            title={t('learningDeleteAiMsg', 'حذف این پاسخ هوش مصنوعی')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <AiFormattedRenderer content={msg.text} forceLtr={forceLtr} />

                      {msg.aiInfo && (msg.aiInfo.model || typeof msg.aiInfo.responseTime === 'number') && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] font-mono text-gray-500 dark:text-gray-400 flex flex-wrap items-center justify-between gap-2 bg-slate-50/70 dark:bg-gray-800/40 p-2.5 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                          <div className="flex items-center gap-1.5 font-bold text-gray-700 dark:text-gray-200">
                            <Cpu className="w-3.5 h-3.5 text-indigo-500" />
                            <span>{t('learningAiModelLabel', 'مدل پردازش هوش مصنوعی (AI Model):')}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2.5 dir-ltr">
                            {msg.aiInfo.model && (
                              <span className="bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60 px-2.5 py-0.5 rounded-md font-semibold text-[11px]">
                                Model: <strong className="text-emerald-800 dark:text-emerald-200 font-mono">{msg.aiInfo.model}</strong>
                              </span>
                            )}
                            {typeof msg.aiInfo.responseTime === 'number' && msg.aiInfo.responseTime > 0 && (
                              <span className="bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 px-2 py-0.5 rounded-md font-semibold text-[11px]">
                                Response Time: <strong className="text-amber-800 dark:text-amber-200 font-mono">{msg.aiInfo.responseTime > 1000 ? `${(msg.aiInfo.responseTime / 1000).toFixed(1)}s` : `${msg.aiInfo.responseTime} ms`}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Interactive Add/Chat Box Console */}
            <div className="bg-white dark:bg-gray-900 border border-indigo-100 dark:border-gray-800 rounded-2xl p-4 md:p-5 shadow-lg space-y-3 sticky bottom-4 z-20">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-indigo-500" />
                  {t('learningAddMsgTitle', 'افزودن مبحث جدید یا یادداشت استاد به این جلسه')}
                </span>
                <span className="text-[10px] text-gray-400">
                  {t('learningAddMsgHint', 'می‌توانید سؤال بپرسید یا متن ارزشمند استاد را چسبانده (Paste) کنید')}
                </span>
              </div>

              {followUpError && (
                <div className="p-2.5 bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-xl text-xs text-red-600 dark:text-red-400">
                  {followUpError}
                </div>
              )}

              <div className="space-y-3">
                {/* Quick Educational Prompt Presets */}
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                  <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 shrink-0 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-amber-500" />
                    {t('learningPromptPresetsTitle', 'پیش‌فرض‌های درخواست کامل:')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFollowUpInput(t('learningPromptSumm', 'لطفاً خلاصه‌ای جامع، ساختاریافته و ریزبه‌ریز از مفاهیم اصلی و نکات مهم این مبحث به همراه طبقه‌بندی کامل ارائه دهید.'))}
                    className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-lg text-[11px] font-bold shrink-0 transition-colors cursor-pointer border border-indigo-200 dark:border-indigo-800"
                  >
                    📝 {t('learningPromptSummBtn', 'خلاصه جامع و نکات اصلی')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFollowUpInput(t('learningPromptVocab', 'لطفاً تمامی تعاریف، اصطلاحات تخصصی و مفاهیم کلیدی این موضوع را در قالب جدول منظم با مثال‌های روشن تحلیل فرمایید.'))}
                    className="px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 text-emerald-700 dark:text-emerald-300 rounded-lg text-[11px] font-bold shrink-0 transition-colors cursor-pointer border border-emerald-200 dark:border-emerald-800"
                  >
                    📊 {t('learningPromptVocabBtn', 'جدول اصطلاحات و تعاریف')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFollowUpInput(t('learningPromptScenario', 'لطفاً سناریوهای واقعی، مثال‌های کاربردی، مراحل گام‌به‌گام یا کدهای نمونه اجرایی مرتبط با این مبحث را شرح دهید.'))}
                    className="px-2.5 py-1 bg-purple-50 dark:bg-purple-950/50 hover:bg-purple-100 text-purple-700 dark:text-purple-300 rounded-lg text-[11px] font-bold shrink-0 transition-colors cursor-pointer border border-purple-200 dark:border-purple-800"
                  >
                    ⚙️ {t('learningPromptScenarioBtn', 'مثال و سناریوی کاربردی')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFollowUpInput(t('learningPromptQuiz', 'لطفاً ۵ پرسش خودآزمایی و تست کاربردی همراه با پاسخ‌نامه تشریحی و راهنمای گام‌به‌گام طراحی فرمایید.'))}
                    className="px-2.5 py-1 bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 text-amber-700 dark:text-amber-300 rounded-lg text-[11px] font-bold shrink-0 transition-colors cursor-pointer border border-amber-200 dark:border-amber-800"
                  >
                    ✍️ {t('learningPromptQuizBtn', 'کوییز و تست با پاسخ تشریحی')}
                  </button>
                  {currentTopic && /زبان|language|svenska|english|deutsch|french|spanish|سوئدی|انگلیسی|آلمانی|فرانسوی/i.test(currentTopic.title) && (
                    <button
                      type="button"
                      onClick={() => setFollowUpInput(t('learningPromptGrammar', 'لطفاً تمامی افعال و کلمات این بخش را با جدول کامل صَرف، گرامر، تلفظ روان فارسی و مثال‌های روزمره تحلیل کن.'))}
                      className="px-2.5 py-1 bg-sky-50 dark:bg-sky-950/50 hover:bg-sky-100 text-sky-700 dark:text-sky-300 rounded-lg text-[11px] font-bold shrink-0 transition-colors cursor-pointer border border-sky-200 dark:border-sky-800"
                    >
                      🌐 {t('learningPromptGrammarBtn', 'جدول گرامر و صَرف زبان')}
                    </button>
                  )}
                </div>

                <textarea
                  rows={3}
                  value={followUpInput}
                  onChange={(e) => setFollowUpInput(e.target.value)}
                  placeholder={t('learningMsgInputPh', 'سوال خود درباره این جلسه را بنویسید یا متن جزوه/کد/نکات استاد یا تمرین‌ها را اینجا چسبانده (Paste) کنید...')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                />

                <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 pt-1">
                  <span className="text-[11px] text-gray-400 hidden sm:inline">
                    {t('learningAttachmentOptions', 'گزینه‌های پیوست و ذخیره‌سازی:')}
                  </span>

                  <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
                    {/* Share Video Link Button */}
                    <button
                      type="button"
                      onClick={() => setIsVideoModalOpen(true)}
                      className="bg-purple-100 dark:bg-purple-950/80 hover:bg-purple-200 text-purple-800 dark:text-purple-300 px-3 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-purple-200 dark:border-purple-800"
                    >
                      <Video className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                      {t('learningShareVideoSm', 'اشتراک ویدیو')}
                    </button>

                    {/* Add File Attachment */}
                    <label className="relative overflow-hidden bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 px-3 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer border border-indigo-200 dark:border-indigo-800">
                      {isUploadingFile ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                          <span>{t('learningUploadingStatus', 'در حال آپلود فایل...')}</span>
                        </>
                      ) : (
                        <>
                          <Paperclip className="w-3.5 h-3.5" />
                          <span>{t('learningAttachDocsSm', 'پیوست فایل (Word / PPT / PDF / عکس)')}</span>
                        </>
                      )}
                      <input 
                        type="file" 
                        multiple 
                        disabled={isUploadingFile}
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.json,.md,.heic,.heif" 
                        onChange={(e) => {
                          const inputEl = e.target;
                          const files = inputEl.files ? Array.from(inputEl.files) : null;
                          if (files && files.length > 0) {
                            handleProcessFiles(files, (att) => {
                              addAttachmentToSession(currentSession.id, att);
                              refreshData();
                            }).finally(() => { if (inputEl) inputEl.value = ''; });
                          }
                        }}
                        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10" 
                      />
                    </label>

                    {/* Add as Direct Teacher Note Button */}
                    <button
                      type="button"
                      onClick={handleAddDirectTeacherNote}
                      disabled={!followUpInput.trim() || loadingFollowUp}
                      className="flex-1 sm:flex-initial bg-amber-500 hover:bg-amber-600 text-white px-3.5 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer disabled:opacity-40"
                    >
                      <StickyNote className="w-3.5 h-3.5" />
                      {t('learningAddTeacherNote', 'افزودن مستقیم جزوه استاد')}
                    </button>

                    {/* Ask AI / Explain Sub-topic Button */}
                    <button
                      type="button"
                      onClick={handleSendFollowUpAi}
                      disabled={!followUpInput.trim() || loadingFollowUp}
                      className="flex-1 sm:flex-initial bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 flex items-center justify-center gap-1.5 transition-all cursor-pointer disabled:opacity-40"
                    >
                      {loadingFollowUp ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                          {t('learningTrainingBtn', 'در حال آموزش و تحلیل...')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                          {t('learningTrainAiBtn', 'تحلیل و آموزش با هوش مصنوعی')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ==================================================================== */}
      {/* 5. SHARED CLOUD FILE STORAGE HUB */}
      {/* ==================================================================== */}
      {activeView === 'file_manager' && (
        <SharedFileManager initialSectionFilter="learning" onClose={() => setActiveView('topics')} />
      )}
      {/* ==================================================================== */}
      {isTopicModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-950/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 animate-scaleUp">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="font-bold text-sm text-gray-900 dark:text-white font-display flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" />
                {editingTopic ? t('learningEditMainTopic', 'ویرایش موضوع اصلی') : t('learningCreateMainTopic', 'ایجاد موضوع اصلی جدید')}
              </h3>
            </div>

            <form onSubmit={handleSaveTopic} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('learningMainTopicTitle', 'عنوان موضوع اصلی')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={topicTitleInput}
                  onChange={(e) => setTopicTitleInput(e.target.value)}
                  placeholder={t('learningMainTopicPh', 'مثلاً: زبان سوئدی (Swedish Language) یا Docker')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('learningMainTopicDesc', 'توضیحات کلی موضوع')}
                </label>
                <textarea
                  rows={3}
                  value={topicDescInput}
                  onChange={(e) => setTopicDescInput(e.target.value)}
                  placeholder={t('learningMainTopicDescPh', 'توضیحات کوتاه یا هدف از یادگیری این موضوع...')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsTopicModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  {t('learningCancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-indigo-500/20 transition-all cursor-pointer"
                >
                  {editingTopic ? t('learningUpdateTopicBtn', 'بروزرسانی موضوع') : t('learningSaveTopicBtn', 'ثبت موضوع')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================================================================== */}
      {/* 6. SHARE VIDEO LINK MODAL */}
      {/* ==================================================================== */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 z-50 bg-gray-950/70 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 pb-3">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 font-display">
                <Video className="w-5 h-5 text-purple-600" />
                {t('learningShareVideoModalTitle', 'اشتراک‌گذاری لینک ویدیو آموزشی')}
              </h3>
              <button
                type="button"
                onClick={() => setIsVideoModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddVideoLinkSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('learningVideoTitleLabel', 'عنوان یا نام ویدیو')}
                </label>
                <input
                  type="text"
                  value={videoTitleInput}
                  onChange={(e) => setVideoTitleInput(e.target.value)}
                  placeholder={t('learningVideoTitlePh', 'مثلاً: فیلم جلسه آموزشی زبان سوئدی / ویدیوی یوتیوب')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('learningVideoUrlLabel', 'آدرس / لینک ویدیو (یوتیوب، آپارات، اینستاگرام، ویمو یا لینک مستقیم)')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  required
                  value={videoUrlInput}
                  onChange={(e) => setVideoUrlInput(e.target.value)}
                  placeholder={t('learningVideoUrlPlaceholder', 'https://www.youtube.com/watch?v=... یا https://www.aparat.com/v/...')}
                  className="w-full bg-slate-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3.5 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono dir-ltr"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {t('learningVideoUrlHint', 'ویدیوهای یوتیوب، آپارات، اینستاگرام و ویمو به صورت مستقیم درون این جلسه قابل پخش خواهند بود.')}
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setIsVideoModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  {t('learningCancel', 'انصراف')}
                </button>
                <button
                  type="submit"
                  disabled={!videoUrlInput.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-md shadow-purple-500/20 transition-all cursor-pointer disabled:opacity-50"
                >
                  {t('learningSaveToSessionBtn', 'ثبت و افزوده شدن به جلسه')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4 animate-scaleUp text-right dir-rtl">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 rounded-2xl flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-base font-bold text-gray-900 dark:text-white font-display">
                {t('learningDeleteModalTitle', 'حذف {{type}}', { type: deleteModalState.type === 'topic' ? t('learningTopicLabel', 'موضوع') : deleteModalState.type === 'session' ? t('learningSessionLabel', 'جلسه') : deleteModalState.type === 'message' ? t('learningMsgLabel', 'پیام / ورودی') : t('learningAttLabel', 'پیوست') })}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {t('learningDeleteModalDesc1', 'آیا از حذف')} <strong className="text-gray-800 dark:text-gray-200">"{deleteModalState.title || t('learningThisItem', 'این مورد')}"</strong> {t('learningDeleteModalDesc2', 'اطمینان دارید؟ این عملیات قابل بازگشت نیست.')}
              </p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteModalState(prev => ({ ...prev, isOpen: false }))}
                className="flex-1 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                {t('learningCancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-xs font-bold shadow-md shadow-red-500/20 transition-all cursor-pointer"
              >
                {t('learningDeleteConfirmBtn', 'حذف قطعی')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Screen File Uploading Lock Overlay */}
      {isUploadingFile && (
        <div className="fixed inset-0 z-[100] bg-gray-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn dir-rtl cursor-wait select-none">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl text-center space-y-5 animate-scaleUp">
            <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-900/60 animate-ping opacity-75"></div>
              <div className="w-20 h-20 rounded-full bg-indigo-50 dark:bg-indigo-950/80 border-2 border-indigo-500/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shadow-inner">
                <UploadCloud className="w-9 h-9 animate-bounce" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-extrabold text-base md:text-lg text-gray-900 dark:text-white">
                {t('learningUploadingTitle', 'در حال آپلود و پردازش فایل آموزشی...')}
              </h3>
              {uploadingFileName && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/50 px-3 py-1.5 rounded-xl inline-block max-w-full truncate dir-ltr">
                  {uploadingFileName}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed pt-1">
                {t('learningUploadingDesc', 'لطفاً شکیبا باشید. فایل شما در حال ذخیره روی سرور و استخراج محتوا جهت تحلیل هوش مصنوعی است.')}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 h-2.5 rounded-full animate-pulse w-full"></div>
            </div>

            <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-gray-400 dark:text-gray-500 pt-1">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500" />
              {t('learningUploadingWait', 'صفحه به طور خودکار پس از پایان آپلود آزاد خواهد شد...')}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
